import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUsers, FiChevronDown, FiChevronRight, FiEdit2, FiTrash2, FiMail, FiBriefcase } from 'react-icons/fi';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? ((parts[0][0] + parts[parts.length - 1][0])).toUpperCase() : parts[0][0].toUpperCase();
}

function UserCard({ m, color, currentUserId, onUserClick, teamName }) {
  const initials = getInitials(m.name);
  const isMe = m.user_id === currentUserId;
  return (
    <motion.div
      whileHover={{ y: -4, boxShadow: '0 12px 32px rgba(0,0,0,0.12)' }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onUserClick?.(m)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '24px 20px 18px', borderRadius: 16, minWidth: 160, maxWidth: 180,
        background: isMe ? `linear-gradient(135deg, ${color}06, ${color}12)` : 'var(--bg-card)',
        border: `2px solid ${isMe ? color + '50' : 'var(--border)'}`,
        cursor: onUserClick ? 'pointer' : 'default',
        boxShadow: isMe ? `0 4px 20px ${color}18` : '0 2px 12px rgba(0,0,0,0.04)',
        transition: 'all 0.2s ease', position: 'relative'
      }}>
      {isMe && (
        <div style={{ position: 'absolute', top: 8, right: 8, fontSize: 8, fontWeight: 800, padding: '2px 6px', borderRadius: 4, background: color, color: '#fff', letterSpacing: '0.5px' }}>YOU</div>
      )}
      {m.avatar ? (
        <img src={`/api/users/avatar/${m.avatar}`} alt="" style={{
          width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', marginBottom: 12,
          border: `3px solid ${isMe ? color + '50' : 'var(--border)'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }} />
      ) : (
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: m.avatar_color || `linear-gradient(135deg, ${color}, ${color}cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 12,
          border: `3px solid ${isMe ? color + '50' : 'transparent'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>{initials}</div>
      )}
      <div style={{
        fontSize: 14, fontWeight: 700, color: isMe ? color : 'var(--text-primary)',
        textAlign: 'center', lineHeight: 1.3, marginBottom: 6,
        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
      }}>
        {m.name}
      </div>
      {teamName && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <FiUsers size={10} /> {teamName}
        </div>
      )}
      {m.kpts?.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
          {m.kpts.map((k, i) => (
            <span key={i} style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
              background: `${k.color || '#6b7280'}12`, color: k.color || '#6b7280',
              border: `1px solid ${k.color || '#6b7280'}25`
            }}>{k.name}</span>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export default function TeamOrgChart({ teams, currentUserId, defaultExpanded, onEdit, onDelete, onUserClick, hideTeamNames, compactMode }) {
  const [expandedTeams, setExpandedTeams] = useState(() => defaultExpanded || new Set());

  const toggleExpand = (id) => setExpandedTeams(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const myTeamId = teams.find(t => t.members?.some(m => m.user_id === currentUserId))?.id;

  const TeamNode = ({ t, depth = 0, isTopLevel = false }) => {
    const children = visibleTeams.filter(c => c.supervised_by === t.id);
    const isMyTeam = currentUserId && t.id === myTeamId;
    const isExpanded = hideTeamNames || expandedTeams.has(t.id);
    const hasChildren = children.length > 0;
    const hasMembers = t.members?.length > 0;
    const color = t.color || '#2563eb';
    const showConnectors = !hideTeamNames || !isTopLevel;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Team header card */}
        {!hideTeamNames && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          whileHover={{ y: -2, boxShadow: '0 8px 28px rgba(0,0,0,0.1)' }}
          style={{
            padding: '16px 24px', borderRadius: 14,
            background: isMyTeam ? `linear-gradient(135deg, ${color}06, ${color}10)` : 'var(--bg-card)',
            border: `2px solid ${isMyTeam ? color : color + '35'}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
            display: 'inline-flex', alignItems: 'center', gap: 14,
            cursor: (hasMembers || hasChildren) ? 'pointer' : 'default',
            minWidth: 200, transition: 'all 0.2s ease'
          }}
          onClick={() => (hasMembers || hasChildren) && toggleExpand(t.id)}>
          <div style={{
            width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: `0 4px 12px ${color}30`
          }}>
            <FiUsers size={20} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{t.name}</span>
              {isMyTeam && <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 5, background: color, color: '#fff', letterSpacing: '0.5px' }}>YOUR TEAM</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              {t.team_number && <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 11, padding: '1px 5px', background: 'var(--bg-input)', borderRadius: 4 }}>{t.team_number}</span>}
              <span>{t.member_count || 0} member{(t.member_count || 0) !== 1 ? 's' : ''}</span>
            </div>
          </div>
          {(hasMembers || hasChildren) && (
            <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
              {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            </span>
          )}
          {onEdit && (
            <motion.button whileTap={{ scale: 0.9 }} onClick={e => { e.stopPropagation(); onEdit(t); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 }}><FiEdit2 size={14} /></motion.button>
          )}
          {onDelete && (
            <motion.button whileTap={{ scale: 0.9 }} onClick={e => { e.stopPropagation(); onDelete(t); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}><FiTrash2 size={14} /></motion.button>
          )}
        </motion.div>
        )}

        {/* Connector line down from team header */}
        <AnimatePresence>
          {isExpanded && (hasMembers || hasChildren) && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Vertical line from header to members */}
              {!hideTeamNames && <div style={{ width: 2, height: 20, background: `linear-gradient(${color}40, ${color}20)`, margin: '0 auto' }} />}

              {/* Member cards in a row */}
              {hasMembers && (
                <div style={{ position: 'relative' }}>
                  {/* Horizontal connector bar */}
                  {showConnectors && t.members.length > 1 && (
                    <div style={{
                      position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                      width: `calc(100% - 180px)`, height: 2, background: `linear-gradient(90deg, transparent, ${color}25, transparent)`
                    }} />
                  )}
                  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', paddingTop: 0 }}>
                    {t.members.map(m => (
                      <div key={m.user_id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {/* Vertical connector to each card */}
                        {showConnectors && <div style={{ width: 2, height: 16, background: color + '20' }} />}
                        <UserCard m={m} color={color} currentUserId={currentUserId} onUserClick={onUserClick} teamName={t.name} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Child teams */}
              {hasChildren && (
                <div style={{ position: 'relative', marginTop: hasMembers ? 28 : 0 }}>
                  {showConnectors && !hasMembers && <div style={{ width: 2, height: 10, background: color + '20', margin: '0 auto' }} />}
                  {showConnectors && hasMembers && <div style={{ width: 2, height: 20, background: color + '20', margin: '0 auto' }} />}
                  {showConnectors && children.length > 1 && (
                    <div style={{
                      width: `calc(100% - 200px)`, maxWidth: children.length * 240,
                      height: 2, background: `linear-gradient(90deg, transparent, ${color}20, transparent)`, margin: '0 auto'
                    }} />
                  )}
                  <div style={{ display: 'flex', gap: 36, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {children.map(child => (
                      <div key={child.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {showConnectors && <div style={{ width: 2, height: 16, background: (child.color || color) + '20' }} />}
                        <TeamNode t={child} depth={depth + 1} isTopLevel={false} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Show all teams including empty ones
  const visibleTeams = teams;

  const topLevel = visibleTeams.filter(t => !t.supervised_by);
  const orphans = visibleTeams.filter(t => t.supervised_by && !visibleTeams.find(x => x.id === t.supervised_by));
  const allRoots = [...topLevel, ...orphans];

  if (allRoots.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
        <FiUsers size={44} style={{ marginBottom: 12, opacity: 0.3 }} />
        <div style={{ fontSize: 15, fontWeight: 600 }}>No teams have been created yet</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Create teams in the Teams tab to build your org chart</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 36, alignItems: 'center', overflowX: 'auto', padding: '16px 0' }}>
      {allRoots.map(t => <TeamNode key={t.id} t={t} depth={0} isTopLevel={true} />)}
    </div>
  );
}
